import { Check, Loader2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import type { Avatar } from "@/types/avatar";
import { cn } from "@/utils/cn";

interface Props {
  avatars: Avatar[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (avatarId: string | null) => void;
}

export const AvatarSelector = ({ selectedId, onSelect, avatars, isLoading }: Props) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="py-4 text-center text-sm text-zinc-400">Loading avatars...</div>
    );
  }

  if (avatars.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-xl border-2 border-zinc-200 border-dashed py-6">
        <p className="mb-2 text-sm text-zinc-500">No avatars yet</p>
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.AVATARS)}
          className="text-primary hover:bg-background hover:text-primary-hover"
        >
          <Plus />
          Create Avatar
        </Button>
      </div>
    );
  }

  return (
    <div>
      <label className="mb-3 block font-medium text-sm text-zinc-700">
        Select Avatar
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {avatars.map((avatar) => {
          const isSelected = selectedId === avatar.id;
          const isGenerating = avatar.status === "generating";
          const isFailed = avatar.status === "failed";
          const isDisabled = isGenerating || isFailed;
          return (
            <button
              key={avatar.id}
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelect(isSelected ? null : avatar.id)}
              className={cn(
                "relative flex flex-col items-center rounded-xl border-2 p-3 transition-all",
                isDisabled
                  ? "cursor-not-allowed border-zinc-100 bg-zinc-50 opacity-60"
                  : isSelected
                    ? "border-primary bg-background shadow-md"
                    : "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm",
              )}
            >
              {isSelected && !isDisabled && (
                <div className="absolute top-2 right-2 rounded-full bg-primary p-0.5">
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}
              {isGenerating && (
                <div className="absolute top-2 right-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              )}
              <img
                src={avatar.portrait_url || avatar.image_url}
                alt={avatar.name}
                className={cn(
                  "mb-2 h-16 w-16 rounded-lg object-cover",
                  isGenerating && "opacity-50",
                )}
              />
              <span className="max-w-full truncate font-medium text-xs text-zinc-900">
                {avatar.name}
              </span>
              {isGenerating && (
                <span className="mt-0.5 text-[10px] text-primary">Generating...</span>
              )}
              {isFailed && (
                <span className="mt-0.5 text-[10px] text-red-500">Failed</span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => navigate(ROUTES.AVATARS)}
          className="flex flex-col items-center justify-center rounded-xl border-2 border-zinc-200 border-dashed p-3 text-zinc-400 transition-all hover:border-zinc-300 hover:text-zinc-600"
        >
          <Plus className="mb-1 h-8 w-8" />
          <span className="font-medium text-xs">New</span>
        </button>
      </div>
    </div>
  );
};
